import { useEffect, useMemo, useState } from "react";
import {
  CHECKBOX_LABEL_POSITIONS,
  CHECKBOX_SIZES,
  Checkbox,
  INPUT_SIZES,
  SearchInput,
} from "@cdkglobal/radial";
import { RenderInventoryBuildingsRoutesCheckboxListProps } from "../../../../../types";
import { dispatchDeletedObjectIfExists } from "../../../helpers";

export default function RenderInventoryBuildingsRoutesCheckboxList({
  propertyKey,
  inputValues,
  optionsList,
  isDisabled,
  rowDataReducerState,
  dispatch,
}: Readonly<RenderInventoryBuildingsRoutesCheckboxListProps>): JSX.Element {
  const [searchValue, setSearchValue] = useState("");
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  useEffect(() => {
    const values = inputValues.map((option) => option.value as string);
    setSelectedValues(values);
  }, [inputValues]);

  const filteredOptions = useMemo(() => {
    return optionsList.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [optionsList, searchValue]);

  const handleToggle = (value: string) => {
    if (isDisabled) return;

    const existingIndex = selectedValues.indexOf(value);
    if (existingIndex >= 0) {
      const updated = selectedValues.filter((selected) => selected !== value);
      setSelectedValues(updated);

      dispatch({
        type: "REMOVE_MULTI_VALUED_FIELD",
        payload: {
          propertyKey,
          propertySubKey: propertyKey,
          rowIndex: existingIndex,
        },
      });

      dispatchDeletedObjectIfExists({
        propertyKey,
        index: existingIndex,
        rowData: rowDataReducerState.rowData,
        dispatch,
      });
      return;
    }

    setSelectedValues((prev) => [...prev, value]);
    dispatch({
      type: "ADD_MULTI_VALUED_FIELD",
      payload: {
        propertyKey,
        propertySubKey: propertyKey,
        newFieldValue: value,
      },
    });
  };

  return (
    <div data-testid={`${propertyKey}-checkbox-list`}>
      <div style={{ width: "100%", marginBottom: "0.75rem" }}>
        <SearchInput
          clearButtonLabel="clear-search"
          dataTestId={`${propertyKey}-checkbox-search`}
          disableBrowserAutocomplete
          id={`${propertyKey}-checkbox-search`}
          isAlwaysExpanded
          name={`${propertyKey}-checkbox-search`}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(event.target.value)
          }
          onClear={() => setSearchValue("")}
          placeholder="Search by Route Name"
          size={INPUT_SIZES.STANDARD}
          value={searchValue}
        />
      </div>

      {filteredOptions.map((option, index) => {
        const value = `${option.value ?? ""}`;
        const isChecked = selectedValues.includes(value);
        return (
          <div
            key={`${value}-${index}`}
            style={{
              marginBottom: "0.9rem",
            }}
          >
            <Checkbox
              checked={isChecked}
              dataTestId={`${propertyKey}-checkbox-item-${index}`}
              id={`${propertyKey}-checkbox-${index}`}
              isDisabled={isDisabled}
              label={option.label}
              labelPosition={CHECKBOX_LABEL_POSITIONS.AFTER}
              name={`${propertyKey}-checkbox-${index}`}
              onChange={() => handleToggle(value)}
              size={CHECKBOX_SIZES.STANDARD}
            />
          </div>
        );
      })}
    </div>
  );
}
